import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOnboardingContext } from '../../context/OnboardingContext';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, Settings, LogOut,
  Shield, Flame,
  Search, Play, Plus, ChefHat,
  Users, Calendar, MessageCircle, Radio, Bot
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import NotificationsDropdown from '../NotificationsDropdown';
import CreateMenu from '../CreateMenu';
import { Onboarding } from '../Onboarding';

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  
  // Onboarding context
  const { showOnboarding, completeOnboarding, isChecked } = useOnboardingContext();

  // Navigation items with translations
  const mainNavItems = [
    { icon: ChefHat, labelKey: 'nav.recipes', path: '/', exact: true, badge: 'NEW' },
    { icon: Play, labelKey: 'nav.feed', path: '/feed' },
    { icon: Search, labelKey: 'nav.search', path: '/search' },
    { icon: Radio, labelKey: 'nav.live', path: '/live' },
    { icon: Trophy, labelKey: 'nav.challenges', path: '/challenges' },
    { icon: Bot, labelKey: 'nav.chefHedi', path: '/chef-hedi', badge: 'IA' },
  ];

  const secondaryNavItems = [
    { icon: Users, labelKey: 'nav.privateChefs', path: '/private-chefs' },
    { icon: Calendar, labelKey: 'nav.seasons', path: '/seasons' },
    { icon: Trophy, labelKey: 'nav.ranking', path: '/ranking' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/landing');
  };

  const adminRoles = ['admin', 'super_admin', 'content_admin', 'community_manager', 'moderator', 'analyst'];
  const isAdmin = user && adminRoles.includes(user.role);

  // Check if current path matches (for exact matching on home)
  const isActiveRoute = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#F8F5F2]">
      {/* Desktop Layout - Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-72 bg-white border-r border-gray-100 flex-col z-40">
        {/* Logo & Brand */}
        <div className="p-5 border-b border-gray-100 flex-shrink-0">
          <NavLink to="/" className="flex items-center gap-3 group">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] flex items-center justify-center shadow-lg shadow-[#1E4ED8]/20 group-hover:shadow-xl transition-shadow flex-shrink-0">
              <span className="text-xl">🌶️</span>
            </div>
            <div className="min-w-0">
              <span className="font-fraunces font-bold text-xl text-gray-900 block truncate">MHARHER</span>
              <span className="text-xs text-gray-500 truncate block">Cuisine Tunisienne</span>
            </div>
          </NavLink>
        </div>

        {/* User Stats Card - Clickable to Profile */}
        <div className="p-3 flex-shrink-0">
          <NavLink 
            to={`/u/${user?.username}`}
            className="block bg-gradient-to-br from-[#F8F5F2] to-white rounded-xl p-3 border border-gray-100 hover:border-[#1E4ED8]/30 hover:shadow-md transition-all"
            data-testid="sidebar-profile-link"
          >
            <div className="flex items-center gap-3 mb-2">
              <Avatar className="w-10 h-10 border-2 border-[#1E4ED8]/20 flex-shrink-0">
                <AvatarImage src={user?.profile_picture} />
                <AvatarFallback className="bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] text-white font-bold text-sm">
                  {user?.username?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="font-semibold text-gray-900 truncate text-sm">{user?.display_name || user?.username}</p>
                <p className="text-xs text-gray-500 truncate">@{user?.username}</p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 bg-gradient-to-r from-[#D9381E]/10 to-[#E07A5F]/10 px-2.5 py-1 rounded-full flex-shrink-0">
                <Flame className="w-3.5 h-3.5 text-[#D9381E]" />
                <span className="font-bold text-sm text-[#D9381E]">{user?.barka || 0}</span>
              </div>
              <Badge variant="outline" className="border-[#1E4ED8]/30 text-[#1E4ED8] capitalize text-xs truncate">
                {t(`levels.${user?.level}`) || t('levels.apprenti')}
              </Badge>
            </div>
          </NavLink>
          
          {/* Quick Wallet Access */}
          <NavLink 
            to="/wallet"
            className="mt-2 flex items-center justify-between bg-gradient-to-r from-orange-500/10 to-red-500/10 hover:from-orange-500/20 hover:to-red-500/20 p-2.5 rounded-xl transition-all"
            data-testid="sidebar-wallet-link"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Flame className="w-4 h-4 text-orange-500 flex-shrink-0" />
              <span className="font-semibold text-gray-800 text-sm truncate">Mon Wallet</span>
            </div>
            <span className="text-orange-500 font-bold text-sm flex-shrink-0">{user?.barka || 0}</span>
          </NavLink>
        </div>

        {/* Main Navigation - Scrollable */}
        <nav className="flex-1 px-3 py-2 overflow-y-auto min-h-0">
          <div className="space-y-1">
            {mainNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 group ${
                    isActive
                      ? 'bg-[#1E4ED8] text-white shadow-lg shadow-[#1E4ED8]/30'
                      : 'text-gray-600 hover:bg-[#1E4ED8]/5 hover:text-[#1E4ED8]'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${
                      isActive ? 'bg-white/20' : 'bg-gray-100 group-hover:bg-[#1E4ED8]/10'
                    }`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <span className="truncate text-sm">{t(item.labelKey)}</span>
                    {item.badge && (
                      <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                        isActive ? 'bg-white/20' : 'bg-[#D9381E]/10 text-[#D9381E]'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          {/* Create Button */}
          <div className="my-3 px-1">
            <Button
              onClick={() => setShowCreateMenu(true)}
              className="w-full h-12 bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6] hover:from-[#1E40AF] hover:to-[#2563EB] text-white rounded-xl shadow-lg shadow-[#1E4ED8]/30 hover:shadow-xl transition-all"
              data-testid="create-btn-desktop"
            >
              <Plus className="w-5 h-5 mr-2" />
              <span className="font-semibold">{t('nav.create')}</span>
            </Button>
          </div>

          {/* Secondary Navigation */}
          <div className="border-t border-gray-100 pt-3 mt-2">
            <p className="px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
              {t('nav.discover')}
            </p>
            <div className="space-y-0.5">
              {secondaryNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-[#1E4ED8]/10 text-[#1E4ED8]'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                    }`
                  }
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </nav>

        {/* Bottom Actions - Fixed */}
        <div className="p-3 border-t border-gray-100 space-y-1 flex-shrink-0">
          {isAdmin && (
            <NavLink
              to="/admin"
              className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-gray-500 hover:bg-[#D9381E]/10 hover:text-[#D9381E] transition-colors"
            >
              <Shield className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{t('nav.admin')}</span>
            </NavLink>
          )}
          <NavLink
            to="/settings"
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
          >
            <Settings className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{t('nav.settings')}</span>
          </NavLink>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-[#D9381E] hover:bg-[#D9381E]/10 transition-colors w-full"
            data-testid="logout-btn-sidebar"
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white/95 backdrop-blur-xl border-b border-gray-100 z-50 shadow-sm">
        <div className="h-full px-3 flex items-center relative">
          {/* Left — Search */}
          <div className="flex items-center flex-shrink-0 z-10">
            <NavLink
              to="/search"
              className={({ isActive }) =>
                `relative p-2 rounded-full flex-shrink-0 transition-colors ${
                  isActive
                    ? 'text-[#1E4ED8]'
                    : 'text-gray-600 hover:bg-[#1E4ED8]/10 hover:text-[#1E4ED8]'
                }`
              }
            >
              <Search className="w-5 h-5" />
            </NavLink>
          </div>

          {/* Center — Brand wordmark, absolutely centered */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <NavLink
              to="/"
              className="font-black text-[#1E4ED8] tracking-widest text-lg uppercase select-none pointer-events-auto"
            >
              MHARHER
            </NavLink>
          </div>

          {/* Right — Messages + Notifications */}
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto z-10">
            <NavLink
              to="/messages"
              className="relative p-2 rounded-full hover:bg-[#1E4ED8]/10 transition-colors"
              data-testid="messages-nav-link"
            >
              <MessageCircle className="w-5 h-5 text-gray-600 hover:text-[#1E4ED8]" />
            </NavLink>
            <NotificationsDropdown />
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="lg:ml-72 pt-14 lg:pt-0 pb-24 lg:pb-8 min-h-screen">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation - Instagram Style */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 z-40 safe-area-pb">
        <div className="flex items-center justify-around h-14 px-1">
          {/* Feed */}
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `flex flex-col items-center justify-center flex-1 h-full transition-all ${
                isActive ? 'text-[#1E4ED8]' : 'text-gray-400'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <motion.div
                  animate={{ scale: isActive ? 1.1 : 1 }}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                    isActive ? 'bg-[#1E4ED8]/10' : ''
                  }`}
                >
                  <ChefHat className={`w-5 h-5 ${isActive ? 'text-[#1E4ED8]' : ''}`} />
                </motion.div>
                <span className="text-[9px] font-medium mt-0.5 truncate max-w-[50px]">{t('nav.recipes')}</span>
              </>
            )}
          </NavLink>

          {/* Feed Video */}
          <NavLink
            to="/feed"
            className={({ isActive }) =>
              `flex flex-col items-center justify-center flex-1 h-full transition-all ${
                isActive ? 'text-[#1E4ED8]' : 'text-gray-400'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <motion.div
                  animate={{ scale: isActive ? 1.1 : 1 }}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                    isActive ? 'bg-[#1E4ED8]/10' : ''
                  }`}
                >
                  <Play className={`w-5 h-5 ${isActive ? 'fill-[#1E4ED8]' : ''}`} />
                </motion.div>
                <span className="text-[9px] font-medium mt-0.5 truncate max-w-[50px]">{t('nav.feed')}</span>
              </>
            )}
          </NavLink>

          {/* Create Button - Center */}
          <button
            onClick={() => setShowCreateMenu(true)}
            className="flex items-center justify-center w-12 h-12 -mt-4 rounded-xl bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] text-white shadow-lg shadow-[#1E4ED8]/30 border-2 border-white"
            data-testid="create-btn-mobile"
          >
            <Plus className="w-6 h-6" />
          </button>

          {/* Défis */}
          <NavLink
            to="/challenges"
            className={({ isActive }) =>
              `flex flex-col items-center justify-center flex-1 h-full transition-all ${
                isActive ? 'text-[#1E4ED8]' : 'text-gray-400'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <motion.div
                  animate={{ scale: isActive ? 1.1 : 1 }}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                    isActive ? 'bg-[#1E4ED8]/10' : ''
                  }`}
                >
                  <Trophy className="w-5 h-5" />
                </motion.div>
                <span className="text-[9px] font-medium mt-0.5 truncate max-w-[50px]">{t('nav.challenges')}</span>
              </>
            )}
          </NavLink>

          {/* Profile */}
          <NavLink
            to={`/u/${user?.username}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center flex-1 h-full transition-all ${
                isActive ? 'text-[#1E4ED8]' : 'text-gray-400'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Avatar className={`w-7 h-7 border-2 ${isActive ? 'border-[#1E4ED8]' : 'border-transparent'}`}>
                  <AvatarImage src={user?.profile_picture} />
                  <AvatarFallback className="bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] text-white text-[10px] font-bold">
                    {user?.username?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-[9px] font-medium mt-0.5 truncate max-w-[50px]">{t('nav.profile')}</span>
              </>
            )}
          </NavLink>
        </div>
      </nav>

      {/* Create Menu Modal */}
      <CreateMenu isOpen={showCreateMenu} onClose={() => setShowCreateMenu(false)} />
      
      {/* Onboarding Modal */}
      <AnimatePresence>
        {showOnboarding && isChecked && (
          <Onboarding onComplete={completeOnboarding} />
        )}
      </AnimatePresence>
    </div>
  );
}

export default AppLayout;
